/**
 * Safe local date parser for YYYY-MM-DD or DD-MM-YYYY or ISO strings
 */
export const parseEventLocalDate = (dateInput) => {
  if (!dateInput) return null;
  if (dateInput instanceof Date && !isNaN(dateInput.getTime())) {
    const d = new Date(dateInput);
    d.setHours(0, 0, 0, 0);
    return d;
  }
  if (typeof dateInput === "string") {
    const cleanStr = dateInput.split("T")[0].trim();
    if (cleanStr.includes("-")) {
      const parts = cleanStr.split("-").map(Number);
      if (parts.length === 3) {
        if (parts[0] > 1000) {
          // YYYY-MM-DD
          return new Date(parts[0], parts[1] - 1, parts[2], 0, 0, 0, 0);
        } else {
          // DD-MM-YYYY
          return new Date(parts[2], parts[1] - 1, parts[0], 0, 0, 0, 0);
        }
      }
    } else if (cleanStr.includes("/")) {
      const parts = cleanStr.split("/").map(Number);
      if (parts.length === 3) {
        if (parts[0] > 1000) {
          return new Date(parts[0], parts[1] - 1, parts[2], 0, 0, 0, 0);
        } else {
          return new Date(parts[2], parts[1] - 1, parts[0], 0, 0, 0, 0);
        }
      }
    }
    const d = new Date(dateInput);
    if (!isNaN(d.getTime())) {
      d.setHours(0, 0, 0, 0);
      return d;
    }
  }
  return null;
};

/**
 * Check if the current date is on or before the event end date.
 * Business Rule:
 *  - Download Banner is only shown till the event end date.
 *  - After end date, it should not be shown.
 */
export const isEventWithinEndDate = (booking) => {
  if (!booking?.end_date) return true;
  const endDate = parseEventLocalDate(booking.end_date);
  if (!endDate) return true;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today <= endDate;
};

/**
 * Check if Prerequisites can be viewed for an event.
 * Business Rule:
 *  - SPOC can ONLY view prerequisites from 5 days before the event start date till the event end date.
 *  - Before (startDate - 5 days): Cannot view.
 *  - After endDate: Cannot view.
 *
 * @param {Object} booking - Event booking object with start_date and optionally end_date
 * @returns {boolean} true if within [startDate - 5 days, endDate]
 */
export const isPrerequisitesViewable = (booking) => {
  if (!booking) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // 1. After end date: cannot view
  if (booking.end_date) {
    const endDate = parseEventLocalDate(booking.end_date);
    if (endDate && today > endDate) {
      return false;
    }
  }

  // 2. Before 5 days prior to start date: cannot view
  if (booking.start_date) {
    const startDate = parseEventLocalDate(booking.start_date);
    if (startDate) {
      const allowedStartDate = new Date(startDate);
      allowedStartDate.setDate(allowedStartDate.getDate() - 5);
      allowedStartDate.setHours(0, 0, 0, 0);

      if (today < allowedStartDate) {
        return false;
      }
    }
  }

  return true;
};

/**
 * Check detailed prerequisites view eligibility with user-friendly messages
 */
export const checkPrerequisitesViewEligibility = (booking) => {
  if (!booking?.start_date) {
    return { canView: true, message: "" };
  }

  const startDate = parseEventLocalDate(booking.start_date);
  const endDate = booking.end_date ? parseEventLocalDate(booking.end_date) : null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (endDate && today > endDate) {
    const formattedEndDate = endDate.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
    return {
      canView: false,
      message: `Prerequisites can no longer be viewed. The event ended on ${formattedEndDate}.`
    };
  }

  if (startDate) {
    const allowedStartDate = new Date(startDate);
    allowedStartDate.setDate(allowedStartDate.getDate() - 5);
    allowedStartDate.setHours(0, 0, 0, 0);

    if (today < allowedStartDate) {
      const formattedAllowedStartDate = allowedStartDate.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      });
      return {
        canView: false,
        message: `Prerequisites can only be viewed from 5 days before the event start date (available from ${formattedAllowedStartDate}) till the event end date.`
      };
    }
  }

  return { canView: true, message: "" };
};

/**
 * Check if the SPOC is eligible to upload geo-tagged photos for an event.
 * Business Rules:
 *  - Tech Camp: From 2nd day (startDate + 1 day) to bookslot end date.
 *  - FDP: From 3rd day (startDate + 2 days) to bookslot end date.
 *  - Later than bookslot end date: Not eligible to upload.
 *
 * @param {Object} booking - Event booking object with start_date and optionally end_date
 * @param {string} eventTypeOverride - "fdp", "edp", "tech_camp", etc.
 * @returns {Object} { canUpload, isExpired, isUpcoming, targetDayNumber, allowedStartDate, formattedAllowedStartDate, formattedEndDate, programLabel, message, reminderMessage, shortNotice }
 */
export const checkGeoPhotoUploadEligibility = (booking, eventTypeOverride) => {
  if (!booking?.start_date) {
    return {
      canUpload: false,
      isExpired: false,
      isUpcoming: false,
      targetDayNumber: 3,
      allowedStartDate: null,
      formattedAllowedStartDate: "",
      formattedEndDate: "",
      programLabel: "FDP",
      message: "Event start date is not available.",
      reminderMessage: "",
      shortNotice: "Date not available"
    };
  }

  const startDate = parseEventLocalDate(booking.start_date);
  if (!startDate) {
    return {
      canUpload: false,
      isExpired: false,
      isUpcoming: false,
      targetDayNumber: 3,
      allowedStartDate: null,
      formattedAllowedStartDate: "",
      formattedEndDate: "",
      programLabel: "FDP",
      message: "Invalid event start date.",
      reminderMessage: "",
      shortNotice: "Invalid date"
    };
  }

  const resolvedType = String(
    eventTypeOverride ||
    booking.event_type ||
    booking.type ||
    ""
  ).trim().toLowerCase();

  const isTechCamp =
    resolvedType === "tech_camp" ||
    resolvedType === "techcamp" ||
    resolvedType === "tech camp";

  // Tech Camp: From 2nd day (startDate + 1 day)
  // FDP: From 3rd day (startDate + 2 days)
  const targetDayNumber = isTechCamp ? 2 : 3;
  const daysToAdd = targetDayNumber - 1;
  const programLabel = isTechCamp ? "Tech Camp" : "FDP";

  const allowedStartDate = new Date(startDate);
  allowedStartDate.setDate(allowedStartDate.getDate() + daysToAdd);
  allowedStartDate.setHours(0, 0, 0, 0);

  const endDate = booking.end_date ? parseEventLocalDate(booking.end_date) : null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const formattedAllowedStartDate = allowedStartDate.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });

  const formattedEndDate = endDate
    ? endDate.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      })
    : "";

  const daySuffix = targetDayNumber === 2 ? "nd" : "rd";

  // Case 1: Past the bookslot end date (Upload Closed)
  if (endDate && today > endDate) {
    return {
      canUpload: false,
      isExpired: true,
      isUpcoming: false,
      targetDayNumber,
      allowedStartDate,
      formattedAllowedStartDate,
      formattedEndDate,
      programLabel,
      message: `Photo upload closed. Photos could only be uploaded till the bookslot end date (${formattedEndDate}). It is no longer eligible to upload.`,
      reminderMessage: "",
      shortNotice: `Upload closed (Ended ${formattedEndDate})`
    };
  }

  // Case 2: Before the starting allowed day (Day 2 for Tech Camp, Day 3 for FDP)
  if (today < allowedStartDate) {
    const endNotice = formattedEndDate ? ` till the bookslot end date (${formattedEndDate})` : "";
    return {
      canUpload: false,
      isExpired: false,
      isUpcoming: true,
      targetDayNumber,
      allowedStartDate,
      formattedAllowedStartDate,
      formattedEndDate,
      programLabel,
      message: `Geo-tagged photos can only be uploaded from the ${targetDayNumber}${daySuffix} day of ${programLabel} (${formattedAllowedStartDate})${endNotice}. Later, it will not be eligible to upload.`,
      reminderMessage: "",
      shortNotice: `Available from Day ${targetDayNumber} (${formattedAllowedStartDate})`
    };
  }

  // Case 3: Within the eligible window (from target day to end date)
  const reminderMessage = formattedEndDate
    ? `You can only upload photos till the event end date (${formattedEndDate}). Later, it will not be eligible to upload.`
    : "You can only upload photos till the event end date. Later, it will not be eligible to upload.";

  return {
    canUpload: true,
    isExpired: false,
    isUpcoming: false,
    targetDayNumber,
    allowedStartDate,
    formattedAllowedStartDate,
    formattedEndDate,
    programLabel,
    message: "",
    reminderMessage,
    shortNotice: formattedEndDate ? `Upload open till ${formattedEndDate}` : "Upload open"
  };
};

/**
 * Reverse geocode latitude and longitude to human-readable address (e.g., "Bhubaneswar, Odisha")
 */
export const reverseGeocodeCoordinates = async (latitude, longitude) => {
  if (!latitude || !longitude) return "";

  // 1. Try OpenStreetMap Nominatim
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3500);

    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=14&addressdetails=1`,
      {
        headers: { "Accept-Language": "en" },
        signal: controller.signal
      }
    );
    clearTimeout(timeoutId);

    if (res.ok) {
      const data = await res.json();
      const addr = data?.address || {};
      const city =
        addr.city ||
        addr.town ||
        addr.village ||
        addr.suburb ||
        addr.county ||
        addr.state_district ||
        "";
      const state = addr.state || "";
      const country = addr.country || "";
      const parts = [city, state].filter(Boolean);
      if (parts.length > 0) return parts.join(", ");
      if (country) return country;
    }
  } catch (err) {
    console.warn("Nominatim reverse geocode error:", err);
  }

  // 2. Fallback to BigDataCloud client API
  try {
    const controller2 = new AbortController();
    const timeoutId2 = setTimeout(() => controller2.abort(), 3500);

    const res2 = await fetch(
      `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`,
      { signal: controller2.signal }
    );
    clearTimeout(timeoutId2);

    if (res2.ok) {
      const data2 = await res2.json();
      const city = data2.city || data2.locality || "";
      const state = data2.principalSubdivision || "";
      const parts = Array.from(new Set([city, state])).filter(Boolean);
      if (parts.length > 0) return parts.join(", ");
    }
  } catch (e) {
    console.warn("Fallback reverse geocode error:", e);
  }

  return "";
};

/**
 * Fetch device GPS coordinates and reverse geocode location seamlessly
 */
export const getDeviceLocation = () => {
  return new Promise((resolve, reject) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      reject(new Error("Geolocation is not supported by your browser."));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const coords = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          timestamp: pos.timestamp,
          address: ""
        };

        try {
          const address = await reverseGeocodeCoordinates(coords.latitude, coords.longitude);
          coords.address = address;
        } catch (e) {
          console.warn("Reverse geocode failed:", e);
        }

        resolve(coords);
      },
      (err) => {
        let msg = "Unable to retrieve GPS location.";
        if (err.code === 1) {
          msg = "Location permission denied. Please allow location access in your browser.";
        } else if (err.code === 2) {
          msg = "GPS location unavailable. Please enable device location.";
        } else if (err.code === 3) {
          msg = "Location request timed out. Please retry.";
        }
        reject(new Error(msg));
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  });
};

/**
 * Draw stylish, high-contrast watermark banner on Canvas
 */
export const drawWatermarkBanner = (ctx, width, height, coords, metadata) => {
  const lat = coords?.latitude;
  const lng = coords?.longitude;
  const accuracy = coords?.accuracy ? Math.round(coords.accuracy) : null;
  const placeName = coords?.address || metadata?.address || "";

  const bannerHeight = Math.max(90, Math.round(height * 0.16));
  const bannerY = height - bannerHeight;

  // Dark gradient background for readability
  const gradient = ctx.createLinearGradient(0, bannerY, 0, height);
  gradient.addColorStop(0, "rgba(15, 23, 42, 0.88)");
  gradient.addColorStop(1, "rgba(15, 23, 42, 0.98)");
  ctx.fillStyle = gradient;
  ctx.fillRect(0, bannerY, width, bannerHeight);

  // Cyan accent line on top of banner
  ctx.fillStyle = "#38bdf8";
  ctx.fillRect(0, bannerY, width, Math.max(3, Math.round(height * 0.005)));

  // Scaled typography
  const titleSize = Math.max(14, Math.round(width * 0.022));
  const subSize = Math.max(11, Math.round(width * 0.017));
  const paddingX = Math.max(16, Math.round(width * 0.025));

  let cursorY = bannerY + Math.max(22, Math.round(bannerHeight * 0.28));

  // 1. GPS Coordinates & Place Name (e.g., "Bhubaneswar, Odisha")
  ctx.font = `bold ${titleSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
  ctx.fillStyle = "#38bdf8";
  const latText = lat !== undefined && lat !== null ? `${Math.abs(lat).toFixed(6)}° ${lat >= 0 ? "N" : "S"}` : "N/A";
  const lngText = lng !== undefined && lng !== null ? `${Math.abs(lng).toFixed(6)}° ${lng >= 0 ? "E" : "W"}` : "N/A";
  const accText = accuracy ? ` (±${accuracy}m)` : "";
  const placeText = placeName ? ` • ${placeName}` : "";
  ctx.fillText(`📍 GPS: ${latText}, ${lngText}${accText}${placeText}`, paddingX, cursorY);

  // 2. Institute & Program
  cursorY += Math.max(18, Math.round(bannerHeight * 0.26));
  ctx.font = `600 ${subSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
  ctx.fillStyle = "#ffffff";
  const instName = metadata?.instituteName || "Institution Event";
  const eventName = metadata?.domainTitle || metadata?.eventType || "Event";
  ctx.fillText(`🏛️ ${instName} • ${eventName}`, paddingX, cursorY);

  // 3. Timestamp
  cursorY += Math.max(16, Math.round(bannerHeight * 0.24));
  ctx.font = `normal ${subSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
  ctx.fillStyle = "#cbd5e1";
  const now = new Date();
  const dateStr = now.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
  const timeStr = now.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  });
  ctx.fillText(`🕒 ${dateStr}, ${timeStr} IST`, paddingX, cursorY);
};

/**
 * Stamp geo-tag onto video capture frame
 */
export const stampGeoTagFromVideo = (videoEl, coords, metadata) => {
  return new Promise((resolve, reject) => {
    try {
      const width = videoEl.videoWidth || 1280;
      const height = videoEl.videoHeight || 720;
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");

      ctx.drawImage(videoEl, 0, 0, width, height);
      drawWatermarkBanner(ctx, width, height, coords, metadata);

      canvas.toBlob(
        (blob) => {
          if (!blob) return reject(new Error("Failed to process camera frame"));
          resolve(blob);
        },
        "image/jpeg",
        0.92
      );
    } catch (err) {
      reject(err);
    }
  });
};

/**
 * Stamp geo-tag onto image file (from native camera)
 */
export const stampGeoTagFromImage = (imageFile, coords, metadata) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(imageFile);
    img.onload = () => {
      try {
        const width = img.naturalWidth || img.width || 1280;
        const height = img.naturalHeight || img.height || 720;
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");

        ctx.drawImage(img, 0, 0, width, height);
        drawWatermarkBanner(ctx, width, height, coords, metadata);

        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(objectUrl);
            if (!blob) return reject(new Error("Failed to stamp photo"));
            resolve(blob);
          },
          "image/jpeg",
          0.92
        );
      } catch (err) {
        URL.revokeObjectURL(objectUrl);
        reject(err);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Failed to load photo for geo-tagging"));
    };
    img.src = objectUrl;
  });
};

/**
 * Detects whether the user is accessing from a mobile or tablet device.
 * Used to restrict live camera photo capturing on laptops and desktop computers.
 */
export const isMobileOrTabletDevice = () => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }
  const ua = navigator.userAgent || navigator.vendor || window.opera || "";
  // Check standard mobile and tablet user agents
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i;
  // Handle iPads running iOS 13+ requesting desktop site (identifies as Macintosh with multi-touch)
  const isIPad = /Macintosh/i.test(ua) && navigator.maxTouchPoints && navigator.maxTouchPoints > 1;
  return mobileRegex.test(ua) || Boolean(isIPad);
};

/**
 * Detects whether the user is accessing from a laptop or desktop computer.
 */
export const isLaptopOrDesktopDevice = () => {
  return !isMobileOrTabletDevice();
};

